<template>
  <div class="bg-white border border-border rounded-md lg:mb-5 mb-4">
    <div class="flex w-full  border-b border-border !m-0 p-4 gap-[8px] items-center">
      <span class="number-badge">
        <span v-if="(state.opt_type != 'general' && state.opt_type != 'SEIU721')">2</span>
        <span v-if="(state.opt_type == 'general' || state.opt_type == 'SEIU721')">3</span>
      </span>
      <h2 class="card-title !m-0">Select a Plan</h2>
    </div>
    <div class="p-4">
      <div v-if="showStudentBillingToggle" class="billing-period-switch-wrap mb-5">
        <div class="billing-period-switch-glass">
          <div class="billing-period-switch" role="group" aria-label="Billing period">
            <button
              type="button"
              class="billing-period-switch__label"
              :class="{ 'billing-period-switch__label--active': state.student_billing_period === 'annual' }"
              @click="setBillingPeriod('annual')"
            >
              Bill Yearly
            </button>

            <button
              type="button"
              class="billing-period-switch__track"
              :class="{ 'billing-period-switch__track--monthly': state.student_billing_period === 'monthly' }"
              role="switch"
              :aria-checked="state.student_billing_period === 'annual'"
              aria-label="Toggle billing period"
              @click="toggleBillingPeriod"
            >
              <span
                class="billing-period-switch__thumb"
                :class="{ 'billing-period-switch__thumb--monthly': state.student_billing_period === 'monthly' }"
              />
            </button>

            <button
              type="button"
              class="billing-period-switch__label"
              :class="{ 'billing-period-switch__label--active': state.student_billing_period === 'monthly' }"
              @click="setBillingPeriod('monthly')"
            >
              Bill Monthly
            </button>
          </div>
        </div>
      </div>

      <div class="justify-start gap-4 grid 2xl:grid-cols-2 xl:grid-cols-2 lg:grid-cols-1 md:grid-cols-2 grid-cols-1"
        v-if="state.disp_enroll_section && !state.disp_err_msg">
        <div v-for="(group, idx) in state.bundled_group" :key="`${group.id}-${idx}`"
          class="plan-card plan-card-glass flex flex-col"
          :class="{ 'plan-card--selected': group.select_plan, 'plan-card--has-discount': showDiscountBadge(group) }">
          <span :id="`plan-a11y-${idx}`" class="sr-only">
            {{ group.description }}.
            {{ state.company?.company_name }}. {{ group.state }}.
            <template v-if="isStudentFlow">
              {{ group.student_premium_label }}: ${{ group.student_display_rate }}.
              <span v-if="group.student_show_discount">Discount: {{ group.student_discount_percent }}%.</span>
            </template>
            <template v-else>
              <span v-if="state.payroll_enrollment !== '1'">
                <span v-if="state.payment_frequency === 1">Annual</span>
                <span v-else-if="state.payment_frequency === 12">Per Month</span>
                premium: ${{ group.monthly_premium }}.
              </span>
              <span v-else>
                Premium: ${{ group.monthly_premium }}.
              </span>
              <span v-if="state.payment_method == 'CC' && !isStudentFlow">
                Prorated Premium: ${{ group.prorated_premium }}.
                <template v-if="planServiceFee(group) > 0">
                  Service Fee: {{ planServiceFee(group).toFixed(2) }}.
                </template>
              </span>
            </template>
            Final Amount:
            <span v-if="state.payment_method != 'Payroll'">${{ group.total }}</span>
            <span v-else>${{ group.total_monthly_prem }}</span>.
          </span>

          <div class="plan-card-header">
            <div
              v-if="showDiscountBadge(group)"
              class="plan-discount-ribbon"
              aria-hidden="true"
            >
              {{ formatDiscountPercent(group) }} OFF
            </div>
            <div class="plan-card-header__top">
              <span class="plan-card-icon">
                <img v-if="getPlanIcon(group) === 'CDL'" src="@/assets/images/truck.svg" alt="CDL">
                <img v-else-if="getPlanIcon(group) === 'FAM'" src="@/assets/images/balance.svg" alt="FAM">
                <img v-else src="@/assets/images/idd.png" alt="IDD">
              </span>
              <div class="plan-card-header__info">
                <h3 class="plan-card-title" :id="`plan-title-${idx}`">{{ group.description }}</h3>
                <div class="plan-card-meta">
                  <span>{{ state.company?.company_name }}</span>
                  <span class="plan-card-meta__dot" aria-hidden="true"></span>
                  <span class="uppercase">{{ group.state }}</span>
                </div>
              </div>
            </div>
            <div class="plan-card-price">
              <span class="plan-card-price__amount">${{ getPlanHeaderPrice(group) }}</span>
              <span class="plan-card-price__period" v-if="state.payroll_enrollment != '1'">
                {{ getPlanHeaderPeriod(group) }}
              </span>
            </div>
          </div>

          <div class="plan-card-body flex-1">
            <div class="plan-card-summary plan-card-summary-glass">
              <div class="flex justify-between mb-2.5" :id="`plan-summary-${idx}`">
                <p class="content !mb-0" v-if="state.payroll_enrollment !== '1'">
                  <template v-if="isStudentFlow">{{ group.student_premium_label }}</template>
                  <template v-else>
                    <span v-if="state.payment_frequency === 1">Annual</span>
                    <span v-else-if="state.payment_frequency === 2">Semi-Annual</span>
                    <span v-else-if="state.payment_frequency === 4">Per Quarter</span>
                    <span v-else-if="state.payment_frequency === 9">9 Pay Periods</span>
                    <span v-else-if="state.payment_frequency === 12">Per Month</span>
                    <span v-else-if="state.payment_frequency === 20">20 Pay Periods</span>
                    <span v-else-if="state.payment_frequency === 22">22 Pay Periods</span>
                    <span v-else-if="state.payment_frequency === 24">Semi-Monthly</span>
                    <span v-else-if="state.payment_frequency === 26">Bi-Weekly</span>
                    <span v-else-if="state.payment_frequency === 52">Per Week</span>
                  </template>
                </p>
                <p class="content !mb-0" v-if="state.payroll_enrollment == '1'">
                  Premium
                </p>
                <span class="content !text-blackPrimary !mb-0 !font-bold">
                  ${{ isStudentFlow ? (state.student_billing_period === 'annual' ? group.student_display_rate : group.student_net_premium || group.student_display_rate) : group.monthly_premium }}
                </span>
              </div>

              <div
                class="flex justify-between mb-2.5"
                v-if="isStudentFlow && state.student_billing_period === 'annual' && group.student_show_discount"
              >
                <p class="content !mb-0 student-discount-label">
                  Discount Amount<span v-if="group.student_discount_percent"> ({{ formatDiscountPercent(group) }})</span>
                </p>
                <span class="content !mb-0 !font-bold student-discount-value">-${{ group.student_discount_amount }}</span>
              </div>

              <div class="flex justify-between mb-2.5" v-if="state.payment_method == 'CC' && !isStudentFlow">
                <p class="content !mb-0">Prorated Premium</p>
                <span class="content !text-blackPrimary !mb-0 !font-bold">${{ group.prorated_premium }}</span>
              </div>

              <div class="flex justify-between mb-2.5" v-if="state.payment_method == 'CC' && planServiceFee(group) > 0">
                <p class="content !mb-0">Service Fee</p>
                <span class="content !text-blackPrimary !mb-0 !font-bold">${{ planServiceFee(group).toFixed(2) }}</span>
              </div>

              <div class="flex justify-between mb-0">
                <p class="content !font-bold !mb-0 !text-blackPrimary">Final Amount</p>
                <span v-if="state.payment_method != 'Payroll'"
                  class="content !mb-0 !font-bold !text-lg !text-primary">${{ group.total }}</span>
                <span v-if="state.payment_method == 'Payroll'"
                  class="content !mb-0 !font-bold !text-lg !text-primary">${{ group.total_monthly_prem }}</span>
              </div>
            </div>
          </div>

          <div class="plan-card-footer" v-if="group.select_plan == true">
            <button
              class="primary-button w-full text-center inline-flex justify-center items-center gap-2"
              :aria-labelledby="`plan-title-${idx} plan-summary-${idx}`"
              :aria-describedby="`plan-a11y-${idx}`"
            >
              <img src="@/assets/images/correct.svg" alt="correct" />
              Selected
            </button>
          </div>
          <div class="plan-card-footer" v-if="group.select_plan == false">
            <button
              class="primary-outline-button plan-select-contrast w-full text-center inline-flex justify-center items-center gap-2"
              :aria-labelledby="`plan-title-${idx} plan-summary-${idx}`"
              :aria-describedby="`plan-a11y-${idx}`"
              @click="selectPlan(state.bundled_group, group)">
              Select
            </button>
          </div>
        </div>
      </div>

    </div>
  </div>
  <SelectedPlanInfoMob :state="state" />
</template>

<script>
import SelectedPlanInfoMob from "./SelectedPlanInfoMob.vue";
import {
  isStudentFlow as checkStudentFlow,
  shouldUseStudentPricing as checkShouldUseStudentPricing,
  resolveServiceFeeAmount,
  formatPlanPriceDisplay,
} from "@/utils/enrollmentFlow";

export default {
  name: "PlanSelectionSection",
  data() {
    return {
    };
  },
  components: {
    SelectedPlanInfoMob,
  },
  props: {
    state: Object,
  },
  emits: ["setChoice", "getPlanListAgain", "updateState", "studentBillingPeriodChange"],
  computed: {
    isStudentFlow() {
      return checkStudentFlow(this.state);
    },
    showStudentBillingToggle() {
      return checkShouldUseStudentPricing(this.state);
    },
  },
  methods: {
    planServiceFee(group) {
      return resolveServiceFeeAmount(this.state, group);
    },
    formatDiscountPercent(group) {
      const percent = Number(group?.student_discount_percent);
      if (!Number.isFinite(percent) || percent <= 0) return "";
      const formatted = Number.isInteger(percent) ? percent : percent.toFixed(1);
      return `${formatted}%`;
    },
    showDiscountBadge(group) {
      return (
        this.isStudentFlow &&
        this.state.student_billing_period === "annual" &&
        group.student_show_discount &&
        Number(group.student_discount_percent) > 0
      );
    },
    getPlanHeaderPrice(group) {
      if (this.isStudentFlow) {
        const raw =
          this.state.student_billing_period === "annual"
            ? group.student_net_premium
            : group.student_display_rate;
        return formatPlanPriceDisplay(raw);
      }
      return formatPlanPriceDisplay(group.planFee ?? group.monthly_premium);
    },
    getPlanHeaderPeriod(group) {
      if (this.isStudentFlow) {
        return group.student_rate_suffix || "/year";
      }
      return "/month";
    },
    getPlanIcon(group) {
      const planType = (group?.planType || "").toString().toUpperCase();
      if (planType === "CDL" || planType === "FAM" || planType === "IDD") {
        return planType;
      }

      const description = (group?.description || "").toString().toUpperCase();
      const planCode = (group?.plan_code || "").toString().toUpperCase();

      if (description.includes("CDL") || planCode.includes("CDL")) return "CDL";
      if (
        description.includes("FAMILY") ||
        description.includes("FAM") ||
        planCode.includes("FAM")
      )
        return "FAM";
      return "IDD";
    },
    setBillingPeriod(period) {
      if (this.state.student_billing_period === period) return;
      this.$emit("studentBillingPeriodChange", period);
    },
    toggleBillingPeriod() {
      const next = this.state.student_billing_period === "annual" ? "monthly" : "annual";
      this.setBillingPeriod(next);
    },
    selectPlan(bundled_group, group) {
      this.$emit("setChoice", bundled_group, group);
    }
  },
};
</script>

<style scoped>
.plan-card {
  position: relative;
  overflow: hidden;
  border-radius: 0.875rem;
  isolation: isolate;
}

.plan-card-glass {
  background: linear-gradient(
    145deg,
    rgba(255, 255, 255, 0.82) 0%,
    rgba(255, 255, 255, 0.58) 45%,
    rgba(230, 236, 248, 0.42) 100%
  );
  border: 1px solid rgba(255, 255, 255, 0.78);
  box-shadow:
    0 1px 0 rgba(255, 255, 255, 0.95) inset,
    0 12px 32px rgba(31, 51, 65, 0.08),
    0 2px 8px rgba(31, 51, 65, 0.04);
}

@supports ((-webkit-backdrop-filter: blur(1px)) or (backdrop-filter: blur(1px))) {
  .plan-card-glass {
    background: linear-gradient(
      145deg,
      rgba(255, 255, 255, 0.66) 0%,
      rgba(255, 255, 255, 0.42) 48%,
      rgba(220, 228, 245, 0.3) 100%
    );
    -webkit-backdrop-filter: blur(18px) saturate(160%);
    backdrop-filter: blur(18px) saturate(160%);
  }
}

.plan-card-glass::before {
  content: "";
  position: absolute;
  inset: 1px;
  border-radius: calc(0.875rem - 1px);
  background: linear-gradient(
    180deg,
    rgba(255, 255, 255, 0.5) 0%,
    rgba(255, 255, 255, 0) 55%
  );
  pointer-events: none;
  z-index: 0;
}

.plan-card--selected {
  border-color: rgba(52, 144, 220, 0.55);
  box-shadow:
    0 1px 0 rgba(255, 255, 255, 0.95) inset,
    0 0 0 1px rgba(52, 144, 220, 0.28),
    0 14px 36px rgba(52, 144, 220, 0.16);
}

.plan-card--has-discount .plan-card-header__info {
  padding-right: 3.5rem;
}

.plan-card-body,
.plan-card-footer {
  position: relative;
  z-index: 1;
}

.plan-card-header {
  position: relative;
  display: flex;
  flex-direction: column;
  gap: 0.875rem;
  padding: 1.125rem 1.125rem 1rem;
  border-bottom: 1px solid rgba(31, 51, 65, 0.08);
}

.plan-card-header__top {
  display: flex;
  align-items: flex-start;
  gap: 0.875rem;
  min-width: 0;
}

.plan-card-icon {
  width: 3.125rem;
  height: 3.125rem;
  flex-shrink: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: 0.625rem;
  background: rgba(255, 255, 255, 0.72);
  border: 1px solid rgba(255, 255, 255, 0.85);
  box-shadow:
    0 1px 0 rgba(255, 255, 255, 0.9) inset,
    0 4px 12px rgba(31, 51, 65, 0.06);
}

.plan-card-icon img {
  width: 1.75rem;
  height: 1.75rem;
  object-fit: contain;
}

.plan-card-header__info {
  flex: 1;
  min-width: 0;
}

.plan-card-title {
  margin: 0 0 0.375rem;
  font-size: 0.9375rem;
  font-weight: 700;
  line-height: 1.35;
  letter-spacing: 0.02em;
  text-transform: uppercase;
  color: #1f3341;
  word-break: break-word;
}

.plan-card-meta {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 0.5rem;
  font-size: 0.8125rem;
  line-height: 1.3;
  color: rgba(31, 51, 65, 0.62);
}

.plan-card-meta__dot {
  width: 0.25rem;
  height: 0.25rem;
  border-radius: 50%;
  background: rgba(31, 51, 65, 0.28);
  flex-shrink: 0;
}

.plan-card-price {
  display: flex;
  align-items: baseline;
  justify-content: flex-end;
  gap: 0.25rem;
  padding-left: 3.875rem;
}

.plan-card-price__amount {
  font-size: 1.375rem;
  font-weight: 800;
  line-height: 1;
  letter-spacing: -0.02em;
  color: #1f3341;
}

.plan-card-price__period {
  font-size: 0.875rem;
  font-weight: 500;
  color: rgba(31, 51, 65, 0.58);
}

.plan-card-body {
  padding: 1rem 1.125rem;
}

.plan-card-summary-glass {
  padding: 1rem;
  border-radius: 0.75rem;
  background: linear-gradient(
    180deg,
    rgba(255, 255, 255, 0.55) 0%,
    rgba(255, 255, 255, 0.28) 100%
  );
  border: 1px solid rgba(255, 255, 255, 0.72);
  box-shadow:
    0 1px 0 rgba(255, 255, 255, 0.85) inset,
    0 6px 18px rgba(31, 51, 65, 0.05);
}

@supports ((-webkit-backdrop-filter: blur(1px)) or (backdrop-filter: blur(1px))) {
  .plan-card-summary-glass {
    -webkit-backdrop-filter: blur(12px) saturate(145%);
    backdrop-filter: blur(12px) saturate(145%);
  }
}

.plan-card-footer {
  padding: 0 1.125rem 1.125rem;
}

.plan-discount-ribbon {
  position: absolute;
  top: 0.625rem;
  right: 0.625rem;
  z-index: 2;
  padding: 0.1875rem 0.4375rem;
  border-radius: 0.25rem;
  font-size: 0.625rem;
  font-weight: 700;
  line-height: 1.2;
  letter-spacing: 0.04em;
  color: #fff;
  white-space: nowrap;
  pointer-events: none;
  background: linear-gradient(165deg, #059669 0%, #047857 100%);
  box-shadow: 0 2px 6px rgba(4, 120, 87, 0.22);
}

.billing-period-switch-wrap {
  display: flex;
  justify-content: center;
  padding: 0.5rem 0 0.25rem;
}

.billing-period-switch-glass {
  position: relative;
  display: inline-flex;
  align-items: center;
  padding: 0.625rem 1.375rem;
  border-radius: 9999px;
  isolation: isolate;
  background: linear-gradient(
    145deg,
    rgba(255, 255, 255, 0.78) 0%,
    rgba(255, 255, 255, 0.48) 42%,
    rgba(220, 228, 245, 0.38) 100%
  );
  border: 1px solid rgba(255, 255, 255, 0.72);
  box-shadow:
    0 1px 0 rgba(255, 255, 255, 0.92) inset,
    0 -1px 0 rgba(31, 51, 65, 0.04) inset,
    0 10px 40px rgba(31, 51, 65, 0.1),
    0 2px 8px rgba(31, 51, 65, 0.05);
}

@supports ((-webkit-backdrop-filter: blur(1px)) or (backdrop-filter: blur(1px))) {
  .billing-period-switch-glass {
    background: linear-gradient(
      145deg,
      rgba(255, 255, 255, 0.62) 0%,
      rgba(255, 255, 255, 0.34) 45%,
      rgba(220, 228, 245, 0.28) 100%
    );
    -webkit-backdrop-filter: blur(20px) saturate(165%);
    backdrop-filter: blur(20px) saturate(165%);
  }
}

.billing-period-switch-glass::before {
  content: "";
  position: absolute;
  inset: 1px;
  border-radius: inherit;
  background: linear-gradient(
    180deg,
    rgba(255, 255, 255, 0.55) 0%,
    rgba(255, 255, 255, 0) 52%
  );
  pointer-events: none;
  z-index: 0;
}

.billing-period-switch-glass::after {
  content: "";
  position: absolute;
  inset: 0;
  border-radius: inherit;
  box-shadow: 0 1px 0 rgba(255, 255, 255, 0.35) inset;
  pointer-events: none;
  z-index: 0;
}

.billing-period-switch {
  position: relative;
  z-index: 1;
  display: inline-flex;
  align-items: center;
  gap: 1rem;
}

.billing-period-switch__label {
  border: none;
  background: transparent;
  padding: 0.125rem 0;
  font-size: 0.9375rem;
  font-weight: 500;
  line-height: 1.2;
  letter-spacing: 0.01em;
  color: rgba(31, 51, 65, 0.45);
  cursor: pointer;
  transition: color 0.25s ease, font-weight 0.25s ease, text-shadow 0.25s ease;
}

.billing-period-switch__label--with-badge {
  display: inline-flex;
  align-items: center;
  gap: 0.5rem;
  flex-wrap: wrap;
  justify-content: flex-end;
}

.billing-period-switch__discount {
  display: inline-block;
  padding: 0.25rem 0.5rem;
  border-radius: 0.25rem;
  font-size: 0.6875rem;
  font-weight: 700;
  line-height: 1.2;
  color: #047857;
  letter-spacing: 0.01em;
  white-space: nowrap;
  background-color: #d1fae5;
}

.billing-period-switch__label--active {
  color: #1f3341;
  font-weight: 700;
  text-shadow: 0 1px 0 rgba(255, 255, 255, 0.8);
}

.billing-period-switch__label:focus-visible,
.billing-period-switch__track:focus-visible {
  outline: 2px solid #ba4224;
  outline-offset: 3px;
}

.billing-period-switch__track {
  position: relative;
  width: 3.375rem;
  height: 1.75rem;
  padding: 0;
  border: 1px solid rgba(255, 255, 255, 0.55);
  border-radius: 9999px;
  background: linear-gradient(
    180deg,
    rgba(31, 51, 65, 0.1) 0%,
    rgba(31, 51, 65, 0.06) 100%
  );
  cursor: pointer;
  flex-shrink: 0;
  box-shadow:
    inset 0 2px 4px rgba(31, 51, 65, 0.12),
    inset 0 -1px 0 rgba(255, 255, 255, 0.45),
    0 1px 2px rgba(255, 255, 255, 0.6);
  transition:
    background 0.28s cubic-bezier(0.4, 0, 0.2, 1),
    border-color 0.28s cubic-bezier(0.4, 0, 0.2, 1),
    box-shadow 0.28s cubic-bezier(0.4, 0, 0.2, 1);
}

@supports ((-webkit-backdrop-filter: blur(1px)) or (backdrop-filter: blur(1px))) {
  .billing-period-switch__track {
    -webkit-backdrop-filter: blur(10px);
    backdrop-filter: blur(10px);
  }
}

.billing-period-switch__track--monthly {
  background: linear-gradient(
    180deg,
    rgba(186, 66, 36, 0.14) 0%,
    rgba(186, 66, 36, 0.08) 100%
  );
  border-color: rgba(255, 255, 255, 0.62);
}

.billing-period-switch__thumb {
  position: absolute;
  top: 50%;
  left: 0.1875rem;
  width: 1.25rem;
  height: 1.25rem;
  border-radius: 50%;
  background: linear-gradient(165deg, #3a5568 0%, #1f3341 55%, #162833 100%);
  transform: translateY(-50%);
  transition:
    left 0.32s cubic-bezier(0.34, 1.2, 0.64, 1),
    transform 0.32s cubic-bezier(0.34, 1.2, 0.64, 1),
    box-shadow 0.32s cubic-bezier(0.4, 0, 0.2, 1);
  box-shadow:
    0 1px 0 rgba(255, 255, 255, 0.38) inset,
    0 4px 14px rgba(31, 51, 65, 0.32),
    0 1px 3px rgba(31, 51, 65, 0.22);
}

.billing-period-switch__thumb::after {
  content: "";
  position: absolute;
  top: 0.2rem;
  left: 0.28rem;
  width: 0.42rem;
  height: 0.22rem;
  border-radius: 50%;
  background: rgba(255, 255, 255, 0.42);
  filter: blur(0.5px);
  pointer-events: none;
}

.billing-period-switch__track:active .billing-period-switch__thumb {
  transform: translateY(-50%) scale(0.96);
}

.billing-period-switch__thumb--monthly {
  left: calc(100% - 1.25rem - 0.1875rem);
}

@media (prefers-reduced-motion: reduce) {
  .billing-period-switch__label,
  .billing-period-switch__track,
  .billing-period-switch__thumb {
    transition: none;
  }
}

/* WCAG: increase contrast for "Select" outline button only */
.plan-select-contrast {
  color: #ba4224 !important;
  border-color: #ba4224 !important;
}

.sr-only {
  position: absolute !important;
  width: 1px !important;
  height: 1px !important;
  padding: 0 !important;
  margin: -1px !important;
  overflow: hidden !important;
  clip: rect(0, 0, 0, 0) !important;
  white-space: nowrap !important;
  border: 0 !important;
}
</style>
